import { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

/**
 * Imperative replacement for window.confirm — promise-returning so the
 * call sites stay the same shape as before:
 *
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: "Delete?", destructive: true }))) return;
 *
 * The Provider mounts a single shadcn <Dialog> reused across every
 * confirmation in the app, so we don't manage modal state per page.
 */
export interface ConfirmOptions {
  title: string;
  description?: React.ReactNode;
  confirmLabel?: string;          // default "Confirm"
  cancelLabel?: string;           // default "Cancel"
  /** Renders the confirm button in destructive (rose) styling. */
  destructive?: boolean;
  /** Hide the Cancel button — turns the dialog into a one-button
   *  acknowledge prompt. Useful as a post-action notice. */
  hideCancel?: boolean;
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>;

const ConfirmContext = createContext<ConfirmFn | null>(null);

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext);
  if (!ctx) {
    throw new Error("useConfirm must be used inside <ConfirmProvider>.");
  }
  return ctx;
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen]       = useState(false);
  const [opts, setOpts]       = useState<ConfirmOptions | null>(null);
  // Stash the resolver so we can settle the promise from the button handlers.
  const resolverRef           = useRef<((ok: boolean) => void) | null>(null);

  const confirm = useCallback<ConfirmFn>((next) => {
    setOpts(next);
    setOpen(true);
    return new Promise<boolean>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  function settle(value: boolean) {
    resolverRef.current?.(value);
    resolverRef.current = null;
    setOpen(false);
  }

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <Dialog open={open} onOpenChange={(next) => { if (!next) settle(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            {/* items-start so a wrapped title sits flush with the icon top
                instead of pushing the icon down. `min-w-0` on the flex
                row + on the title span lets the title actually shrink
                inside the modal. */}
            <DialogTitle className="flex min-w-0 items-start gap-2">
              {opts?.destructive && (
                <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                  <AlertTriangle className="h-4 w-4" />
                </span>
              )}
              {/* `overflow-wrap: anywhere` lets long unbroken filenames
                  (e.g. ACT_Communicative_English_Language_Skills_...)
                  wrap mid-word so the title can't push past the modal
                  edge. `break-words` covers normal spaces. */}
              <span className="min-w-0 flex-1 break-words [overflow-wrap:anywhere]">
                {opts?.title}
              </span>
            </DialogTitle>
            {opts?.description && (
              <DialogDescription className="break-words pt-1 [overflow-wrap:anywhere]">
                {opts.description}
              </DialogDescription>
            )}
          </DialogHeader>

          <DialogFooter>
            {!opts?.hideCancel && (
              <Button type="button" variant="ghost" onClick={() => settle(false)}>
                {opts?.cancelLabel ?? "Cancel"}
              </Button>
            )}
            <Button
              type="button"
              autoFocus
              onClick={() => settle(true)}
              className={cn(
                opts?.destructive
                  && "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              )}
            >
              {opts?.confirmLabel ?? "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </ConfirmContext.Provider>
  );
}
